import { SEARCH_DATA_KEY } from 'api/search'
import { useNavigate } from 'react-router-dom'
import React, { MouseEvent } from 'react'
import { computeStatusIfAuto } from 'utils/dealCalculations'
import { useSnackbarContext } from '../../../context/SnackbarProvider/SnackbarProvider'
import { usePostDeal } from './usePostDeal'
import { OrganizationTableItem, Product } from 'types'
import { useQueryClient } from '@tanstack/react-query'
import { useSyncQueryCache } from '../organizations_table/useSyncQueryCache'
import { isVersionableDeal, isRootType } from 'utils/agreementVersioning'

interface SaveAndNavigateArgs {
  event: MouseEvent<HTMLButtonElement>
  newDeal: OrganizationTableItem
  assignedProducts: Product[]
  setNewDeal: React.Dispatch<React.SetStateAction<Record<string, any>>>
  setIsCreating: React.Dispatch<React.SetStateAction<boolean>>
  resetDealState: () => void
  onClose: () => void
  setIsError: React.Dispatch<React.SetStateAction<boolean>>
  validateFields: () => boolean
  cloneMode?: string
}
type _OldTypes = {
  deals: OrganizationTableItem[]
  organizations: OrganizationTableItem[]
  products: OrganizationTableItem[]
}

export const useSaveDealAndNavigate = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { mutateAsync: createDeal } = usePostDeal()
  const { showSnackbar } = useSnackbarContext()
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}')
  const { syncDealsCache, syncProductCache } = useSyncQueryCache()
  const handleSaveDealAndNavigate = ({
    event,
    newDeal,
    assignedProducts = [],
    setNewDeal,
    setIsCreating,
    resetDealState,
    onClose,
    setIsError,
    validateFields,
    cloneMode,
  }: SaveAndNavigateArgs) => {
    event.preventDefault()
    if (!validateFields()) return
    setNewDeal(prev => ({
      ...prev,
      status: 'Draft',
    }))
    const ALL_ITEMS_KEY = SEARCH_DATA_KEY
    setIsCreating(true)
    const updatedProducts = assignedProducts?.map(product => {
      const start = product?.startDate || newDeal?.startDate
      const end = product?.endDate || newDeal?.endDate
      return {
        ...product,
        startDate: start,
        endDate: end,
        dateRange: [start, end],
        status: computeStatusIfAuto(product?.status, start, end),
      }
    })
    const updatedState = {
      linksGenerated: false,
      isCreated: false,
      isApproved: false,
      isCompleted: false,
      isRequested: false,
      isReverted: false,
      requestor: [],
      approvers: [],
      targetLaunchDate: newDeal?.startDate,
      dependenciesDueDate: newDeal?.startDate,
    }
    // Set master agreement versioning
    const versionFields: Record<string, string> = {}
    if (isVersionableDeal(newDeal as any) && isRootType(newDeal?.type ?? '') && !newDeal?.predecessorId && !newDeal?.masterAgreementId) {
      // Brand new root deal — assign 1.0.0
      versionFields.semanticVersion = '1.0.0'
      versionFields.masterAgreementId = newDeal?.deal_uuid ?? crypto.randomUUID?.() ?? `${Date.now()}`
    } else if (newDeal?.masterAgreementId && newDeal?.semanticVersion) {
      // Versioning flow — component already calculated these
      versionFields.semanticVersion = newDeal.semanticVersion as string
      versionFields.masterAgreementId = newDeal.masterAgreementId as string
    }

    // [DEPRECATED] Patch versions should use apply-patch-version route instead of creating new deals.
    // This path is kept for backward compatibility only.
    const isPatchVersion = (newDeal as any)?.versionType === 'patch'
    if (isPatchVersion) {
      console.warn('[DEPRECATED] Patch version via deal creation — use apply-patch-version route instead')
    }

    const updatedDeal = {
      ...newDeal,
      ...updatedState,
      ...versionFields,
      owner: currentUser ? [currentUser?.user_uuid] : [],
      createdBy: currentUser ? currentUser?.user_uuid : '',
      stage: isPatchVersion ? 'Deal Won' : newDeal?.noSignature ? 'Deal Won' : newDeal.stage,
      status: isPatchVersion ? 'Completed' : newDeal?.noSignature ? 'Completed Internally' : newDeal.status,
      dealStatus: isPatchVersion ? 'Active' : (newDeal as any)?.dealStatus,
    }
    console.log('updatedDeal', updatedDeal)
    createDeal(
      {
        dealData: {
          ...updatedDeal,
        },
        products: updatedProducts?.length ? updatedProducts : undefined, // ✅ send separately
        currentUser: currentUser,
      },
      {
        // onSuccess: data => {
        //   console.log('data', data)
        //   setIsCreating(false)
        //   resetDealState()
        //   queryClient.invalidateQueries({ queryKey: ['product', data?.deal?.PK] })
        //   syncDealsCache(queryClient, data?.deal)
        //   if (data && data?.updatedProducts?.length > 0) syncProductCache(queryClient, data?.updatedProducts)
        //   navigate(`/deals/${data?.deal?.PK}/${data?.deal?.SK}/agreement`)
        //   showSnackbar(cloneMode ? 'Deal cloned successfully' : 'Deal created successfully', 'success')
        //   onClose()
        // },
        onSuccess: data => {
          setIsCreating(false)
          resetDealState()

          const createdDeal = data?.deal
          const createdProductsRaw = data?.updatedProducts ?? []
          const createdProducts = createdProductsRaw.map(p => ({
            ...p,
            deal_uuid: p.deal_uuid ?? createdDeal?.deal_uuid,
          }))

          // 1) Keep your existing invalidation if you like
          queryClient.invalidateQueries({ queryKey: ['product', createdDeal?.PK] })

          // 2) Seed the item-by-PK/SK cache used by useGetItemByPkSk
          queryClient.setQueryData(['item', createdDeal.PK, createdDeal.SK], (old: object) => ({
            ...(old ?? {}),
            ...createdDeal,
          }))

          // 3) Seed the big “all items” cache used by useEntitiesFromAllItems
          queryClient.setQueryData(ALL_ITEMS_KEY, (old: any) => {
            const prev = old ?? { deals: [], products: [], organizations: [] }
            const deals = [createdDeal, ...prev.deals.filter((d: any) => d.SK !== createdDeal.SK)]

            // de-dupe by SK (or whatever is canonical for your products)
            const seen = new Set<string>(prev.products.map((p: any) => p.SK))
            const mergedProducts = [...prev.products, ...createdProducts.filter((p: any) => !seen.has(p.SK))]

            return { ...prev, deals, products: mergedProducts }
          })

          // (Optional) also keep your existing helpers
          syncDealsCache(queryClient, createdDeal)
          if (createdProducts.length) syncProductCache(queryClient, createdProducts)

          // 4) Optionally reconcile with server in the background
          // queryClient.invalidateQueries({ queryKey: ALL_ITEMS_KEY, exact: false })

          // 5) Navigate
          navigate(`/deals/${createdDeal.PK}/${createdDeal.SK}/agreement`)
          showSnackbar(
            cloneMode === 'version'
              ? `Version ${updatedDeal.semanticVersion || ''} created successfully`
              : cloneMode
              ? 'Deal cloned successfully'
              : 'Deal created successfully',
            'success'
          )
          onClose()
        },
        onError: () => {
          setIsCreating(false)
          setIsError(true)
        },
      }
    )
  }

  return { handleSaveDealAndNavigate }
}
